import React from 'react';
import { MapPin, Calendar, Gift, AlertCircle, Phone } from 'lucide-react';
import { GoogleBusinessContent } from '../types/socialMedia.types';
import CharacterCounter from '../components/CharacterCounter';
import ScheduleSettings from '../components/ScheduleSettings';
import { PLATFORMS } from '../config/platformConfig';

interface GoogleBusinessEditorProps {
  content: GoogleBusinessContent;
  isEditing: boolean;
  onChange: (field: keyof GoogleBusinessContent, value: any) => void;
}

export default function GoogleBusinessEditor({
  content,
  isEditing,
  onChange
}: GoogleBusinessEditorProps) {
  const platform = PLATFORMS.googlebusiness;

  const renderPostTypeFields = () => {
    switch (content.postType) {
      case 'Update':
        return (
          <>
            {/* Update Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Update Text
              </label>
              <textarea
                value={content.text || ''}
                onChange={(e) => onChange('text', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-32"
                placeholder="Share news about your business..."
                maxLength={4500}
              />
              <CharacterCounter
                current={content.text?.length || 0}
                limit={4500}
                label="Update text"
              />
            </div>

            {/* Call to Action */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="inline w-4 h-4 mr-1" />
                Call to Action
              </label>
              <select
                value={content.callToAction}
                onChange={(e) => onChange('callToAction', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">No Button</option>
                <option value="book">Book</option>
                <option value="order">Order Online</option>
                <option value="buy">Buy</option>
                <option value="learn_more">Learn More</option>
                <option value="sign_up">Sign Up</option>
                <option value="get_offer">Get Offer</option>
                <option value="call">Call Now</option>
                <option value="reserve">Reserve</option>
              </select>
            </div>
          </>
        );

      case 'Event':
        return (
          <>
            {/* Event Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="inline w-4 h-4 mr-1" />
                Event Title
              </label>
              <input
                type="text"
                value={content.eventTitle}
                onChange={(e) => onChange('eventTitle', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Event name..."
                maxLength={58}
              />
              <CharacterCounter
                current={content.eventTitle.length}
                limit={58}
                label="Event title"
                showProgressBar={false}
              />
            </div>

            {/* Event Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Date
                </label>
                <input
                  type="date"
                  value={content.eventDate}
                  onChange={(e) => onChange('eventDate', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Time
                </label>
                <input
                  type="time"
                  value={content.eventTime}
                  onChange={(e) => onChange('eventTime', e.target.value)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>

            {/* Event Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Description
              </label>
              <textarea
                value={content.text || ''}
                onChange={(e) => onChange('text', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-24"
                placeholder="Event details..."
                maxLength={1500}
              />
              <CharacterCounter
                current={content.text?.length || 0}
                limit={1500}
                label="Event description"
              />
            </div>
          </>
        );

      case 'Offer':
        return (
          <>
            {/* Offer Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Gift className="inline w-4 h-4 mr-1" />
                Offer Title
              </label>
              <input
                type="text"
                value={content.offerTitle}
                onChange={(e) => onChange('offerTitle', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="20% Off..."
                maxLength={58}
              />
              <CharacterCounter
                current={content.offerTitle.length}
                limit={58}
                label="Offer title"
                showProgressBar={false}
              />
            </div>

            {/* Offer Terms */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                value={content.offerTerms}
                onChange={(e) => onChange('offerTerms', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-16"
                placeholder="Valid through... Restrictions apply..."
                maxLength={120}
              />
              <CharacterCounter
                current={content.offerTerms.length}
                limit={120}
                label="Offer terms"
                showProgressBar={false}
              />
            </div>

            {/* Offer Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Offer Details
              </label>
              <textarea
                value={content.text || ''}
                onChange={(e) => onChange('text', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-24"
                placeholder="Describe your offer..."
                maxLength={1500}
              />
              <CharacterCounter
                current={content.text?.length || 0}
                limit={1500}
                label="Offer description"
              />
            </div>
          </>
        );

      case 'COVID Update':
        return (
          <>
            <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-xs text-yellow-800">
                  Use this post type to share important COVID-19 related updates
                  about your business operations, safety measures, or service changes.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                COVID-19 Update
              </label>
              <textarea
                value={content.text || ''}
                onChange={(e) => onChange('text', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-32"
                placeholder="Share COVID-19 related updates..."
                maxLength={4500}
              />
              <CharacterCounter
                current={content.text?.length || 0}
                limit={4500}
                label="Update text"
              />
            </div>
          </>
        );

      default:
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Post Content
            </label>
            <textarea
              value={content.text || ''}
              onChange={(e) => onChange('text', e.target.value)}
              disabled={!isEditing}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 h-32"
              placeholder="Share updates about your business..."
              maxLength={4500}
            />
            <CharacterCounter
              current={content.text?.length || 0}
              limit={4500}
              label="Post text"
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Post Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="inline w-4 h-4 mr-1" />
          Post Type
        </label>
        <select
          value={content.postType}
          onChange={(e) => onChange('postType', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        >
          {platform.postTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Choose the type of update for your Google Business Profile
        </p>
      </div>

      {/* Dynamic Fields Based on Post Type */}
      {renderPostTypeFields()}

      {/* Schedule */}
      <ScheduleSettings
        scheduledDate={content.scheduledDate}
        scheduledTime={content.scheduledTime}
        onDateChange={(date) => onChange('scheduledDate', date)}
        onTimeChange={(time) => onChange('scheduledTime', time)}
        disabled={!isEditing}
        platformName="Google Business"
      />
    </div>
  );
}